import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/sections/PageHero";
import { CtaBand } from "@/components/sections/CtaBand";
import { useArticles } from "@/hooks/useArticles";
import hero from "@/assets/hero-radgivning.webp";

const quotes = [
  { quote: "Cloud är min högra hand.", who: "Marie Hällgren", company: "Fredrik Hällgrens Åkeri" },
  { quote: "Utan Cloud skulle jag aldrig ha startat ett företag.", who: "Klara Westerberg", company: "Westerberg Konsult" },
  { quote: "Cloud Redovisning håller oss alltid up-to-date.", who: "Andreas Bodén", company: "Hållbar Teknik" },
];

const Kundreferenser = () => {
  const cases = useArticles("referenser");

  return (
    <>
    <SEO
        title="Kundreferenser för redovisning & rådgivning | Cloud"
        description="Läs kundreferenser från företag i Umeå, Stockholm och Vännäs som fått hjälp med bokföring, lön, rådgivning och digital omställning."
      />
      <PageHero
        eyebrow="Referenser"
        title={<>Företag som <span className="italic text-primary">växer med oss</span>.</>}
        description="Här kan du läsa om samarbeten med företag i Umeå, Stockholm, Vännäs och andra delar av Sverige — från byggbolag till caféer och tjänsteföretag."
        image={hero}
        imageAlt="Kundreferenser för Cloud Redovisning"
      />

      <section className="py-20 md:py-28">
        <div className="container-page grid gap-10 md:gap-12 md:grid-cols-2">
          {cases.map((c, i) => (
            <Link
              to={`/kundreferenser/${c.slug}`}
              key={c.slug}
              className={`group block ${i % 2 === 1 ? "md:mt-16" : ""}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden rounded-sm bg-background-deep">
                {c.hero && (
                  <img
                    src={c.hero}
                    alt={c.title}
                    className="h-full w-full object-contain transition-transform duration-1000 group-hover:scale-[1.04]"
                    loading="lazy"
                    width={1280}
                    height={960}
                    decoding="async"
                  />
                )}
                <div className="absolute inset-0 ring-1 ring-foreground/5" />
                <div className="absolute top-4 left-4 font-display italic text-xs text-background bg-foreground/80 backdrop-blur-md px-3 py-1.5 rounded-full">
                  Kundresa 0{i + 1}
                </div>
              </div>
              <div className="mt-6">
                <h3 className="font-display text-2xl md:text-3xl text-foreground leading-tight group-hover:text-primary transition-colors">
                  {c.title}
                </h3>
                <p className="mt-3 text-foreground-muted leading-relaxed">{c.excerpt}</p>
                <span className="mt-5 link-underline text-sm">
                  Läs kundberättelsen <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="py-20 md:py-28 border-t border-border bg-background-deep">
        <div className="container-page">
          <div className="mb-12">
            <div className="eyebrow">— Fler röster</div>
            <h2 className="display-lg mt-6 text-foreground">Mer från våra kunder.</h2>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {quotes.map((q) => (
              <div
                key={q.who}
                className="bg-card border border-border rounded-sm p-8 transition-all hover:border-foreground/30 hover:shadow-soft"
              >
                <Quote className="h-6 w-6 text-accent mb-5" strokeWidth={1.5} />
                <p className="font-display text-xl text-foreground italic leading-snug">{q.quote}</p>
                <div className="mt-6 pt-6 border-t border-border">
                  <div className="font-medium text-foreground">{q.who}</div>
                  <div className="text-sm text-foreground-muted mt-0.5">{q.company}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaBand title="Vill ni bli nästa kundberättelse?" body="Boka ett första möte — vi ser fram emot att höra om era ambitioner." />
    </>
  );
};

export default Kundreferenser;
